import Vue from 'vue';
import VueRouter from 'vue-router';

import AppLayout from '@/layouts/AppLayout.vue';
import PurchaseLayout from '@/layouts/PurchaseLayout.vue';
import LandingLayout from '@/layouts/LandingLayout.vue';
import LandingIndex from '@/views/landing/indexLanding.vue';
import Success from '@/views/SuccessView.vue';
import Login from '@/views/LoginView.vue';
import LogoutView from '@/views/LogoutView.vue';
import Signup from '@/views/SignupView.vue';
import CreateNewVacationContract from '@/views/landing/CreateNewVacationContractLanding.vue';
import store from '@/store';
import MainLowerPart from '@/views/landing/MainLowerPartLanding.vue';
import configEnv from '@/enums/configEnv';
import LogService from "@/services/LogService";

const enabledPages = configEnv.enabled_pages;
const langs = configEnv.allowed_langs;
const langsP = configEnv.allowed_langs_pattern;
const langsPortal = configEnv.allowed_langs_portal;
const langsPPortal = configEnv.allowed_langs_pattern_portal;
LogService.log('langsPortal', langsPortal, 'langs', langs);

Vue.use(VueRouter);

const routes = [
  {
    path: '/',
    name: 'home',
    component: () => import('@/views/HomeView'),
  },
  {
    path: '/home/logout',
    name: 'logout',
    component: LogoutView,
  },
  {
    path: `/home/:lang(${langsP})?/create-new-vacation-contract`,
    name: 'create-new-vacation-contract',
    component: CreateNewVacationContract,
    meta: {
      allowedLangs: langs,
    },
  },
  {
    path: `/home/:lang(${langsP})?/`,
    component: LandingLayout,
    meta: {
      allowedLangs: langs,
    },
    children: [
      {
        path: '',
        component: LandingIndex,
        children: [
          {
            path: '',
            component: MainLowerPart,
            children: [
              {
                path: '',
                name: 'main',
                component: () => import(/* webpackChunkName: "hotelPage" */ '@/views/landing/MainLanding.vue'),

              },
            ],
          },
        ],
      },
      {
        path: '',
        component: LandingIndex,
        children: [
          {
            path: 'vacation',
            name: 'vacation',
            component: () => import(/* webpackChunkName: "vacationPage" */ '@/views/landing/VacationLanding.vue'),
          },
          {
            path: 'regenversicherung-urlaub',
            name: 'regenversicherung-urlaub',
            component: () => import(/* webpackChunkName: "vacationPage" */ '@/views/landing/VacationLanding.vue'),
          },
        ],
      },
      {
        path: 'contract-withdrawal',
        name: 'contract-withdrawal',
        component: () => import(/* webpackChunkName: "ContractWithDrawal" */ '@/views/ContractWithDrawal.vue'),
      },
    ],
  },
  {
    path: `/home/:lang(${langsPPortal})?/`,
    name: 'app',
    component: AppLayout,
    meta: {
      allowedLangs: langsPortal,
    },
    children: [
      {
        path: 'settings',
        name: 'settings',
        component: () => import(/* webpackChunkName: "Settings" */ '@/views/SettingsView.vue'),
      },
      {
        path: `${configEnv.insurance_page_name}`,
        name: `${configEnv.insurance_page_name}`,
        component: () => import(/* webpackChunkName: "Insurance" */ '@/views/InsuranceView.vue'),
      },
      {
        path: `${configEnv.insurance_page_name}/:id/`,
        name: `${configEnv.insurance_page_name}-id`,
        component: () => import(/* webpackChunkName: "InsuranceId" */ '@/views/InsuranceIdView.vue'),
      },
      {
        path: '404',
        name: '404',
        component: () => import(/* webpackChunkName: "404" */ '@/views/404View.vue'),
      },
    ],
  },
  {
    path: `/home/:lang(${langsP})?/`,
    name: 'app-purchase',
    component: PurchaseLayout,
    meta: {
      allowedLangs: langs,
    },
    children: [
      {
        path: 'buy-vacation',
        name: 'buy-vacation',
        component: () => import(/* webpackChunkName: "BuyVacation" */ '@/views/BuyVacationView.vue'),
      },
      {
        path: 'finalize-vacation',
        name: 'finalize-vacation',
        component: () => import(/* webpackChunkName: "BuyVacation" */ '@/views/FinalizeVacationView.vue'),
      },
      {
        path: 'signup',
        name: 'signup',
        component: Signup,
      },
      {
        path: 'success',
        name: 'success',
        component: Success,
      },
    ],
  },
  {
    path: `/home/:lang(${langsPPortal})?/`,
    name: 'login-screens',
    component: PurchaseLayout,
    meta: {
      allowedLangs: langsPortal,
    },
    children: [
      {
        path: 'login',
        name: 'login',
        component: Login,
      },
      {
        path: 'forgot',
        name: 'forgot',
        component: () => import(/* webpackChunkName: "Forgot" */ '@/views/ForgotView.vue'),
      },
      {
        path: 'password-reset',
        name: 'password-reset',
        component: () => import(/* webpackChunkName: "PasswordReset" */ '@/views/PasswordResetView.vue'),
      },
    ],
  },
  {
    path: '*',
    redirect: () => '/home/de/regenversicherung-urlaub',
  },
];

const router = new VueRouter({
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
  mode: 'history',
  routes,
});

const isBot = () => {
  if (typeof window === 'undefined') return false;
  const userAgent = navigator.userAgent.toLowerCase();
  return /bot|googlebot|crawler|spider|robot|bingbot|google-inspectiontool|lighthouse/i.test(userAgent);
};

router.beforeEach((to, from, next) => {
  const isCrawler = isBot();

  const langFromParams = to.params.lang;
  const langFromPath = to.path.split('/')[2];
  const allLangs = [...new Set([...langs, ...langsPortal])];
  const lang = langFromParams || (allLangs.includes(langFromPath) ? langFromPath : configEnv.default_wp_language);
  // set current lang if route changed
  if (isCrawler) {
    store.commit('SET_LANG', lang); // Direkte Mutation statt Action
  } else {
    store.dispatch('updLang', lang);
  }

  // check for disabled
  if (enabledPages.length === 0 && configEnv.disabled_pages.includes(to.name)) {
    window.location.href = configEnv.wpApiServer;
    return;
  }

  // check for enabled
  if (enabledPages.length > 0 && !enabledPages.includes(to.name)) {
    window.location.href = configEnv.wpApiServer;
    return;
  }

  next();
});

router.afterEach((to) => {
  if ('parentIFrame' in window) {
    // disable autoresize
    window.parentIFrame.autoResize(false);
    setTimeout(() => {
      // set height to zero
      window.parentIFrame.size(0);
    }, 50);
    // enable autoresize
    setTimeout(() => {
      window.parentIFrame.autoResize(true);
      // send the message for update
      window.parentIFrame.sendMessage({
        type: 'route',
        message: to.path,
      });
    }, 400);
  }
});

export default router;
